/**
 * MarginDashboard.jsx — KPI tiles + low/high-margin tables, designed to
 * justify the iter-126 landed-cost work the moment a CFO opens the app.
 *
 * Layout choices:
 *   - 4 KPI tiles up top (avg margin, costed coverage, negative-margin
 *     count, missing landed-cost count). The two "missing" KPIs are
 *     clickable filters into the table below so an operator can drill
 *     in immediately.
 *   - Distribution histogram as a horizontal bar so the shape of the
 *     catalogue's margin profile is legible at a glance.
 *   - Two sortable tables side-by-side on desktop (lowest 20 / highest
 *     20). On mobile they stack.
 *   - Search box + class filter on the lowest-margin table because that's
 *     where the operator will be hunting.
 */
import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  ChartLineUp, Warning, Coins, Tag, ArrowDown, ArrowUp,
  MagnifyingGlass, ArrowClockwise, Lightning,
} from '@phosphor-icons/react';
import api, { applyFloor, applyFloorBulk, undoFloor } from '../lib/api';

const STATUS_BADGE = {
  ok: 'bg-emerald-100 text-emerald-800',
  negative: 'bg-rose-100 text-rose-800',
  uncosted: 'bg-amber-100 text-amber-800',
  unpriced: 'bg-slate-200 text-slate-700',
};

function MarginRow({ row, onApplyFloor, applying }) {
  const showFloor = row.suggested_price_floor !== null && row.suggested_price_floor !== undefined;
  const canApply = row.at_or_below_floor && row.unit_price < row.suggested_price_floor;
  return (
    <tr data-testid={`margin-row-${row.sku_code}`} className="border-b border-border last:border-b-0">
      <td className="p-2">
        <div className="font-mono font-bold text-sm flex items-center gap-1.5">
          {row.sku_code}
          {row.at_or_below_floor && (
            <span className="text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 bg-rose-600 text-white" title="Sale price is at or below your suggested price floor">
              below floor
            </span>
          )}
        </div>
        <div className="text-[11px] text-muted-foreground truncate max-w-[180px]">{row.name}</div>
      </td>
      <td className="text-right p-2 font-mono">${row.unit_price.toFixed(2)}</td>
      <td className="text-right p-2 font-mono text-amber-700">${row.landed_cost.toFixed(2)}</td>
      <td className="text-right p-2 font-mono">
        {showFloor ? (
          <span className={row.at_or_below_floor ? 'text-rose-700 font-bold' : 'text-emerald-700'}>
            ${row.suggested_price_floor.toFixed(2)}
          </span>
        ) : <span className="text-muted-foreground">—</span>}
      </td>
      <td className="text-right p-2 font-mono">
        {row.margin_pct === null ? (
          <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 ${STATUS_BADGE[row.status]}`}>
            {row.status}
          </span>
        ) : (
          <span className={`font-bold ${row.margin_pct < 0 ? 'text-rose-700' : row.margin_pct < 20 ? 'text-amber-700' : 'text-emerald-700'}`}>
            {row.margin_pct.toFixed(1)}%
          </span>
        )}
      </td>
      <td className="text-right p-2 font-mono text-xs">${row.margin_dollars.toFixed(2)}</td>
      <td className="text-right p-2">
        {canApply && onApplyFloor ? (
          <button
            type="button"
            onClick={() => onApplyFloor(row)}
            disabled={applying}
            data-testid={`apply-floor-${row.sku_code}`}
            title={`Set sale price to floor ($${row.suggested_price_floor.toFixed(2)})`}
            className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {applying ? '…' : 'Apply'}
          </button>
        ) : null}
      </td>
    </tr>
  );
}

function MarginTable({ title, icon: Icon, rows, sort, testId, emptyHint, onApplyFloor, applyingId }) {
  return (
    <div className="border border-border bg-card" data-testid={testId}>
      <div className="px-3 py-2 border-b border-border bg-muted/40 flex items-center gap-2">
        <Icon size={16} weight="duotone" />
        <span className="font-bold text-sm uppercase tracking-wider">{title}</span>
        {sort === 'asc' && <ArrowDown size={12} weight="bold" className="text-rose-600" />}
        {sort === 'desc' && <ArrowUp size={12} weight="bold" className="text-emerald-600" />}
      </div>
      <table className="w-full text-xs">
        <thead className="bg-muted/30">
          <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="text-left p-2">SKU / Name</th>
            <th className="text-right p-2">Sale</th>
            <th className="text-right p-2">Landed</th>
            <th className="text-right p-2" title="Suggested price floor = landed × (1 + target margin %)">Floor</th>
            <th className="text-right p-2">Margin</th>
            <th className="text-right p-2">$ / unit</th>
            <th className="text-right p-2"></th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={7} className="p-6 text-center text-muted-foreground text-xs">{emptyHint}</td></tr>
          ) : (
            rows.map((r) => (
              <MarginRow
                key={r.id}
                row={r}
                onApplyFloor={onApplyFloor}
                applying={applyingId === r.id}
              />
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

const KPI_COLOR = {
  'foreground': 'text-foreground',
  'rose-700': 'text-rose-700',
  'amber-700': 'text-amber-700',
  'emerald-700': 'text-emerald-700',
};

function KpiTile({ label, value, sub, color = 'foreground', icon: Icon, onClick, testId }) {
  const Tag = onClick ? 'button' : 'div';
  // Use a static class map — Tailwind JIT can't see runtime-built strings
  // like `text-${color}` and would purge them from the prod bundle.
  const colorClass = KPI_COLOR[color] || KPI_COLOR.foreground;
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      data-testid={testId}
      className={`text-left border border-border bg-card p-3 ${onClick ? 'hover:bg-muted/50 cursor-pointer transition' : ''}`}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
        {Icon && <Icon size={12} weight="duotone" />}
        {label}
      </div>
      <div className={`text-2xl font-mono font-bold mt-1 ${colorClass}`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </Tag>
  );
}

export default function MarginDashboard() {
  const [summary, setSummary] = useState(null);
  const [lowest, setLowest] = useState([]);
  const [highest, setHighest] = useState([]);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState(''); // '', 'negative', 'uncosted', 'unpriced'
  const [belowFloorOnly, setBelowFloorOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  // Editable inline — shown next to the "Below floor" KPI. Mirrors the
  // server value but lets the operator tweak it with a debounced PATCH.
  const [targetPct, setTargetPct] = useState('30');
  const [savingTarget, setSavingTarget] = useState(false);
  // Per-row apply: tracks which SKU id is currently being applied so the
  // button can show a spinner without blocking the rest of the table.
  const [applyingId, setApplyingId] = useState(null);
  const [bulkApplying, setBulkApplying] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const params = { limit: 20 };
      if (search) params.search = search;
      if (filterStatus) params.status_filter = filterStatus;
      if (belowFloorOnly) params.below_floor_only = true;
      const [sRes, lowRes, highRes] = await Promise.all([
        api.get('/analytics/margins/summary'),
        api.get('/analytics/margins', { params: { ...params, sort: 'asc' } }),
        api.get('/analytics/margins', { params: { ...params, sort: 'desc', status_filter: 'ok' } }),
      ]);
      setSummary(sRes.data);
      setLowest(lowRes.data.items || []);
      setHighest(highRes.data.items || []);
      // Sync local target editor with the persisted value (only when not
      // mid-edit so we don't clobber the operator's typing).
      if (!savingTarget) {
        setTargetPct(String(sRes.data.min_target_margin_pct ?? 30));
      }
    } catch (e) {
      toast.error('Failed to load margin data');
    } finally {
      setLoading(false);
    }
  };

  // Single mount/effect — also re-fires on filter changes with a 350ms
  // debounce on search.
  useEffect(() => {
    const t = setTimeout(() => load(), search ? 350 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, filterStatus, belowFloorOnly]);

  const saveTargetPct = async () => {
    // Strip anything that isn't part of a number to defend against pastes
    // like "50%" or "30 ". Reject if nothing numeric remains.
    const cleaned = String(targetPct).trim().replace(/[^\d.\-]/g, '');
    const n = parseFloat(cleaned);
    if (Number.isNaN(n) || n < 0 || n > 500) {
      toast.error('Target margin must be a number between 0 and 500');
      return;
    }
    try {
      setSavingTarget(true);
      await api.patch('/companies/me/settings/margin', { min_target_margin_pct: n });
      toast.success(`Target margin set to ${n}%`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save target');
    } finally {
      setSavingTarget(false);
    }
  };

  const handleUndo = async (skuId, skuCode) => {
    try {
      const res = await undoFloor(skuId);
      const d = res.data || {};
      if (d.undone) {
        toast.success(`Undone · ${skuCode}: $${d.previous_unit_price.toFixed(2)} → $${d.restored_to.toFixed(2)}`);
        await load();
        return;
      }
      // Soft-fail: surface the reason so operators understand why
      const reasonText = {
        no_apply_floor_history: 'Nothing to undo',
        outside_undo_window: 'Undo window expired (5 min)',
        superseded: `Can't undo — newer change (${d.newer_change_mode || 'edit'}) was made after`,
        price_drifted: 'Price was edited elsewhere — undo refused to clobber it',
      }[d.reason] || d.reason || 'Undo failed';
      toast.error(reasonText);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Undo failed');
    }
  };

  const handleApplyFloor = async (row) => {
    if (!window.confirm(
      `Bump sale price for ${row.sku_code} from $${row.unit_price.toFixed(2)} to the suggested floor of $${row.suggested_price_floor.toFixed(2)}?\n\nThis updates the SKU's unit_price and writes a manual price-change entry to the audit log.`
    )) return;
    try {
      setApplyingId(row.id);
      const res = await applyFloor(row.id, { floor: row.suggested_price_floor });
      if (res.data?.applied) {
        // Toast with inline Undo action — auto-disappears with the toast
        // (sonner default ~4s, override to 8s here so the operator has
        // time to react). Server-side undo window is 5 minutes so manual
        // undo via the row stays available too.
        toast.success(
          `${row.sku_code}: $${res.data.previous_unit_price.toFixed(2)} → $${res.data.new_unit_price.toFixed(2)}`,
          {
            duration: 8000,
            action: {
              label: 'Undo',
              onClick: () => handleUndo(row.id, row.sku_code),
            },
          },
        );
      } else {
        toast.info(res.data?.reason === 'already_above_floor'
          ? `${row.sku_code} already meets the floor.`
          : `Skipped: ${res.data?.reason}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed');
    } finally {
      setApplyingId(null);
    }
  };

  const handleBulkApply = async () => {
    const n = summary?.below_floor_count || 0;
    if (n === 0) { toast.info('Nothing to apply — every costed SKU is already at or above the floor.'); return; }
    if (!window.confirm(
      `Bump sale price for all ${n} below-floor SKU${n === 1 ? '' : 's'} up to their suggested floor?\n\nEach price change is audit-logged. SKUs already at or above the floor are skipped automatically.`
    )) return;
    try {
      setBulkApplying(true);
      const res = await applyFloorBulk({});
      const a = res.data?.applied_count || 0;
      const s = res.data?.skipped_count || 0;
      // Build the list of applied SKU IDs so a single Undo-All tap can
      // walk back through them in reverse order. Server still enforces
      // the 5-minute window per SKU; manual edits between apply and undo
      // are detected and refused safely.
      const appliedIds = (res.data?.results || [])
        .filter((r) => r.applied)
        .map((r) => ({ id: r.id, sku_code: r.sku_code }));
      toast.success(
        `Applied to ${a} SKU${a === 1 ? '' : 's'}${s > 0 ? ` · ${s} skipped` : ''}`,
        appliedIds.length > 0
          ? {
              duration: 12000,
              action: {
                label: `Undo all ${appliedIds.length}`,
                onClick: async () => {
                  let undone = 0, failed = 0;
                  // Reverse so the most recent change is reverted first.
                  for (const it of [...appliedIds].reverse()) {
                    try {
                      const r = await undoFloor(it.id);
                      if (r.data?.undone) undone += 1; else failed += 1;
                    } catch (_) { failed += 1; }
                  }
                  toast[undone > 0 ? 'success' : 'error'](
                    `Undo complete · ${undone} restored${failed > 0 ? ` · ${failed} skipped` : ''}`,
                  );
                  await load();
                },
              },
            }
          : { duration: 6000 },
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk apply failed');
    } finally {
      setBulkApplying(false);
    }
  };

  const dist = summary?.distribution || [];
  const distMax = useMemo(() => Math.max(1, ...dist.map((b) => b.count)), [dist]);

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="margin-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="p-2.5 bg-emerald-100 rounded">
              <ChartLineUp size={28} weight="duotone" className="text-emerald-700" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Margin Dashboard</h1>
              <p className="text-muted-foreground">
                Gross margin per SKU using <strong>sale price − landed cost</strong>. Updates whenever a container is finalized.
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="margin-refresh-btn" className="rounded-none">
            <ArrowClockwise size={14} weight="bold" className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* KPI tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <KpiTile
            label="Avg margin"
            value={summary?.avg_margin_pct === null || summary?.avg_margin_pct === undefined
              ? '—' : `${summary.avg_margin_pct.toFixed(1)}%`}
            sub={summary ? `Median ${summary.median_margin_pct?.toFixed?.(1) ?? '—'}%` : ''}
            color={summary?.avg_margin_pct < 0 ? 'rose-700' : summary?.avg_margin_pct < 20 ? 'amber-700' : 'emerald-700'}
            icon={ChartLineUp}
            testId="kpi-avg-margin"
          />
          <KpiTile
            label="Costed SKUs"
            value={summary ? `${summary.costed_skus}` : '—'}
            sub={summary ? `of ${summary.total_skus} total` : ''}
            icon={Tag}
            testId="kpi-costed"
          />
          <KpiTile
            label="Negative margin"
            value={summary?.negative_margin_count ?? 0}
            sub="Selling at a loss"
            color={(summary?.negative_margin_count || 0) > 0 ? 'rose-700' : 'emerald-700'}
            icon={Warning}
            onClick={() => { setBelowFloorOnly(false); setFilterStatus(filterStatus === 'negative' ? '' : 'negative'); }}
            testId="kpi-negative"
          />
          <KpiTile
            label="Below floor"
            value={summary?.below_floor_count ?? 0}
            sub={summary ? `Sale ≤ landed × ${(1 + (summary.min_target_margin_pct || 0) / 100).toFixed(2)}` : ''}
            color={(summary?.below_floor_count || 0) > 0 ? 'rose-700' : 'emerald-700'}
            icon={Warning}
            onClick={() => { setFilterStatus(''); setBelowFloorOnly(!belowFloorOnly); }}
            testId="kpi-below-floor"
          />
          <KpiTile
            label="Missing landed cost"
            value={summary?.missing_landed_cost ?? 0}
            sub="Finalize a container or set manually"
            color={(summary?.missing_landed_cost || 0) > 0 ? 'amber-700' : 'emerald-700'}
            icon={Coins}
            onClick={() => { setBelowFloorOnly(false); setFilterStatus(filterStatus === 'uncosted' ? '' : 'uncosted'); }}
            testId="kpi-missing-landed"
          />
        </div>

        {/* Target-margin editor — drives the "Floor" column on tables */}
        <div className="border border-border bg-card p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="target-margin-editor">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Target margin</div>
            <p className="text-xs text-muted-foreground">
              Used to compute the <strong>Suggested price floor</strong> per SKU: <code className="font-mono text-[11px] bg-muted px-1">landed × (1 + target%)</code>. SKUs whose sale price is at or below the floor get flagged.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              step="1"
              min={0}
              max={500}
              value={targetPct}
              onChange={(e) => setTargetPct(e.target.value)}
              className="w-24 font-mono rounded-none"
              data-testid="target-margin-input"
            />
            <span className="text-sm text-muted-foreground">%</span>
            <Button
              variant="outline"
              onClick={saveTargetPct}
              disabled={savingTarget || (summary && parseFloat(targetPct) === summary.min_target_margin_pct)}
              data-testid="target-margin-save-btn"
              className="rounded-none"
            >
              {savingTarget ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>

        {/* Distribution histogram */}
        <div className="border border-border bg-card p-4">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-3">Margin distribution</div>
          <div className="space-y-1.5">
            {dist.map((b, i) => (
              <div key={b.label} className="grid grid-cols-[80px_1fr_50px] items-center gap-2 text-xs" data-testid={`dist-bucket-${i}`}>
                <div className="font-mono text-right">{b.label}</div>
                <div className="bg-muted h-5 relative overflow-hidden">
                  <div
                    className={`h-full transition-all ${
                      i === 0 ? 'bg-rose-500'
                      : i === 1 ? 'bg-amber-500'
                      : i === 2 ? 'bg-amber-400'
                      : i === 3 ? 'bg-sky-500'
                      : i === 4 ? 'bg-emerald-500'
                      : 'bg-emerald-700'
                    }`}
                    style={{ width: `${(b.count / distMax) * 100}%` }}
                  />
                </div>
                <div className="font-mono text-right">{b.count}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search SKU code or name…"
              className="pl-7 h-9 rounded-none"
              data-testid="margin-search-input"
            />
          </div>
          {filterStatus && (
            <button
              onClick={() => setFilterStatus('')}
              className="text-xs px-2 py-1 bg-amber-100 text-amber-900 hover:bg-amber-200 transition uppercase tracking-wider"
              data-testid="margin-clear-filter"
            >
              filter: {filterStatus} ✕
            </button>
          )}
          {belowFloorOnly && (
            <button
              onClick={() => setBelowFloorOnly(false)}
              className="text-xs px-2 py-1 bg-rose-100 text-rose-900 hover:bg-rose-200 transition uppercase tracking-wider"
              data-testid="margin-clear-floor-filter"
            >
              filter: below floor ✕
            </button>
          )}
          {/* Bulk Apply visibility: shown only when there's at least one
              SKU we can actually move (strict `<` against floor). The
              backend `below_floor_count` uses `<=` so an exact-floor SKU
              still counts as flagged but produces a no-op apply; hiding
              the button in that case avoids the confusing "Applied to 0"
              toast. */}
          {(() => {
            const actionable = lowest.filter((r) => r.at_or_below_floor && r.unit_price < r.suggested_price_floor);
            if (actionable.length === 0) return null;
            return (
              <Button
                type="button"
                onClick={handleBulkApply}
                disabled={bulkApplying}
                data-testid="bulk-apply-floor-btn"
                className="ml-auto rounded-none bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Lightning size={14} weight="bold" className="mr-2" />
                {bulkApplying
                  ? 'Applying…'
                  : `Apply floor to all ${actionable.length} below-floor SKU${actionable.length === 1 ? '' : 's'}`}
              </Button>
            );
          })()}
        </div>

        {/* Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <MarginTable
            title="Lowest 20 margins"
            icon={ArrowDown}
            rows={lowest}
            sort="asc"
            testId="margin-lowest-table"
            onApplyFloor={handleApplyFloor}
            applyingId={applyingId}
            emptyHint={
              filterStatus
                ? `No SKUs match status=${filterStatus}.`
                : (summary?.costed_skus ?? 0) === 0
                  ? 'No SKUs have both a sale price and landed cost set yet. Finalize a container or set landed_cost manually on Inventory.'
                  : 'No matching SKUs.'
            }
          />
          <MarginTable
            title="Highest 20 margins"
            icon={ArrowUp}
            rows={highest}
            sort="desc"
            testId="margin-highest-table"
            onApplyFloor={handleApplyFloor}
            applyingId={applyingId}
            emptyHint={(summary?.costed_skus ?? 0) === 0
              ? 'Awaiting first costed SKU.'
              : 'No matching SKUs.'}
          />
        </div>
      </div>
    </Layout>
  );
}
